import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { getHeaders } from '../../lib/requests';
import { LinkIcon, InformationCircleIcon } from '@heroicons/react/24/outline';

function ItemDropdown({ label, id, name, value, onSelect, items, disabled, showLabels = true, showItemLink = false, showAllergenInfo = false, showCategory = true, showSubcategory = true }) {
  const [internalItems, setInternalItems] = useState(items);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [allergenModalOpen, setAllergenModalOpen] = useState(false);

  // Custom sort function: dietary_restriction_type, then category, then type, then name (all asc)
  const customSort = (a, b) => {
    const dietaryA = (a.dietary_restriction_type || "").toLowerCase();
    const dietaryB = (b.dietary_restriction_type || "").toLowerCase();
    let cmp = dietaryA.localeCompare(dietaryB);
    if (cmp !== 0) return cmp;
    const catA = (a.category || "").toLowerCase();
    const catB = (b.category || "").toLowerCase();
    cmp = catA.localeCompare(catB);
    if (cmp !== 0) return cmp;
    const typeA = (a.type || "").toLowerCase();
    const typeB = (b.type || "").toLowerCase();
    cmp = typeA.localeCompare(typeB);
    if (cmp !== 0) return cmp;
    const nameA = (a.name || "").toLowerCase();
    const nameB = (b.name || "").toLowerCase();
    return nameA.localeCompare(nameB);
  };

  const fetchItems = async () => {
    try {
      const apiToken = localStorage.getItem('accessToken');
      const apiUrl = process.env.REACT_APP_API_URL;
      // This could possibly not be fetching all items here, we should correct and only select the ones we need
      const response = await axios.get(`${apiUrl}/items?pageSize=1000`, {
        headers: getHeaders(apiToken),
      });
      setInternalItems(response.data.data.sort(customSort));
    } catch (error) {
      console.error('Error fetching items', error);
    }
  };

  useEffect(() => {
    if (!items.length && !internalItems.length) {
      fetchItems();
    } else if (items.length) {
      setInternalItems([...items].sort(customSort));
    }
  }, [items]);

  useEffect(() => {
    if (value) {
      const selectedItem = internalItems.find(item => item.item_id === value);
      if (selectedItem) {
        setSelectedCategory(selectedItem.category || '');
        setSelectedType(selectedItem.type || '');
      }
    }
  }, [value, internalItems]);

  const selectedItem = internalItems.find(item => item.item_id === value);

  const uniqueCategories = [...new Set(internalItems.map(item => item.category))].sort();
  const uniqueTypes = [...new Set(internalItems.map(item => item.type))].sort();

  const handleChange = (event) => {
    const selectedItemId = event.target.value;
    const selectedItem = internalItems.find(item => item.item_id === selectedItemId);
    onSelect(selectedItem ? selectedItem.item_id : null);
  };

  const handleCategoryChange = (event) => {
    setSelectedCategory(event.target.value);
  };

  const handleTypeChange = (event) => {
    setSelectedType(event.target.value);
  };

  const filteredItems = internalItems.filter(item =>
    (selectedCategory === '' || item.category === selectedCategory) &&
    (selectedType === '' || item.type === selectedType)
  );

  return (
    <>
      <div className="flex flex-wrap -mx-2 mb-4">
        <div className="px-2 w-full md:w-3/5">
          {showLabels && (
            <label htmlFor={id} className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
              {label}
            </label>
          )}
          <div className="flex items-center">
            <select
              id={id}
              name={name}
              value={value}
              onChange={handleChange}
              className={`bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 ${disabled ? 'bg-gray-200' : ''}`}
              disabled={disabled}
            >
              <option value="">Select Item</option>
              {filteredItems.map((item) => (
                <option key={item.item_id} value={item.item_id}>
                  {`${item.name} - ${item.category} - ${item.type}` +
                    (item.dietary_restriction_type && item.dietary_restriction_type.toLowerCase() !== 'none'
                      ? ` (${item.dietary_restriction_type})`
                      : '')}
                </option>
              ))}
            </select>
            {showAllergenInfo && value && selectedItem?.allergen_note && selectedItem.allergen_note.trim() !== '' && (
              <button
                type="button"
                onClick={() => setAllergenModalOpen(true)}
                className="ml-2 text-blue-500 hover:text-blue-700 focus:outline-none"
                title="View Allergen Note"
              >
                <InformationCircleIcon className="h-4 w-4" aria-hidden="true" />
              </button>
            )}
            {showItemLink && value && (
              <a
                href={`/items/${value}`}
                target={value}
                rel="noopener noreferrer"
                className="ml-3 text-blue-500 hover:text-blue-700 flex items-center"
              >
                <LinkIcon className="h-4 w-4" aria-hidden="true" title="View Item" />
              </a>
            )}
          </div>
        </div>
        {showCategory && (
          <div className="px-2 w-full md:w-1/5">
            {showLabels && (
              <label htmlFor="typeFilter" className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                Category / Type Filter
              </label>
            )}
            <select
              value={selectedType}
              onChange={handleTypeChange}
              className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
              disabled={disabled}
            >
              <option value="">All Types</option>
              {uniqueTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
        )}
        {showSubcategory && (
          <div className="px-2 w-full md:w-1/5">
            {showLabels && (
              <label htmlFor="categoryFilter" className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                Subcategory Filter
              </label>
            )}
            <select
              value={selectedCategory}
              onChange={handleCategoryChange}
              className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
              disabled={disabled}
            >
              <option value="">All Categories</option>
              {uniqueCategories.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          </div>
        )}
      </div>
      {allergenModalOpen && (
        <>
          <div className="fixed inset-0 bg-black bg-opacity-35 z-50" onClick={() => setAllergenModalOpen(false)}></div>
          <div className="fixed inset-0 flex justify-center items-center z-50">
            <div className="bg-white rounded-lg shadow p-4 max-w-md w-full">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-semibold text-gray-900">Allergen Information</h3>
                <button
                  onClick={() => setAllergenModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600 focus:outline-none"
                >
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
              <div>
                <p className="text-gray-700">{selectedItem.allergen_note}</p>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}

ItemDropdown.propTypes = {
  label: PropTypes.string,
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onSelect: PropTypes.func.isRequired,
  items: PropTypes.array.isRequired,
  disabled: PropTypes.bool,
  showLabels: PropTypes.bool,
  showItemLink: PropTypes.bool,
  showAllergenInfo: PropTypes.bool,
  showCategory: PropTypes.bool,
  showSubcategory: PropTypes.bool,
};

ItemDropdown.defaultProps = {
  disabled: false,
  showLabels: true,
  showItemLink: false,
  showAllergenInfo: false,
  showCategory: true,
  showSubcategory: true,
};

export default ItemDropdown;
